// Import wizard (fixed-visual-blueprint Phase 12 Task 27, R7.4/R7.5/R7.6/R7.8/R19.1/R19.3) —
// "Upload My Template" → extract + map → confirm side-by-side → publish. Launched ONLY from
// TemplateControl (DashboardPage.jsx, relocated from AutoDeck.jsx's TemplateBanner 2026-09-04)
// — never from any other surface (R21.1).
//
// Scope note (why there's no raw-column re-picker here): `POST /blueprint/import` resolves each
// field to exactly ONE candidate + a confidence score (`map_reference.py`'s ladder), not a list
// of alternatives to choose from — there is no backend data to drive a "pick a different column"
// dropdown. So this wizard's confirmation surface is what the backend can actually support: per
// Visual, review the side-by-side original-vs-preview, its confidence, edit its display TITLE
// (R7.5), or EXCLUDE it from publish (R7.8's "reorder/remove a graph, fix a mapping, rename a
// label" — title-rename and remove are implemented; a full remap picker is a fast-follow once/if
// the backend returns multiple ranked candidates per field).
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { RefreshCw, Eye, EyeOff } from "lucide-react";
import { useBlueprintImport } from "./BlueprintImportContext";
import { ChartView, fmtValue } from "./AutoDeck";
import { ConfirmDialog, useConfirm } from "../components/feedback";
import { issuesForTable, notesForTable, issueCellLine, tableSummary } from "./tableIssues";
import TableFilterChips from "./TableFilterChips";
import {
  reconcileGate, tablesReconcileGate, tableForPublish, reconcileCellLine, chartFilterGate,
  reconcileRequestFailed, tableStatusLine, reconcileGroupTitle, sourceLabelFor, sourceChoiceState,
} from "./tableFilters";

// Staged progress copy for the upload/extract wait (real testing against the actual
// RO2_FRUTAS_DE_CHILE_KIWI_MACRO_2026.xlsm reference — 19 charts — took ~19-30s server-side:
// upload to S3, then extract+map+preview/reconcile every chart against Athena). A single static
// line for that whole span reads as a hang (a real report from testing: "nothing happens... but
// looks like it works"). These stages don't reflect exact internal timing — they're just enough
// visible progress that the UI never looks frozen during a long, silent wait.
const UPLOAD_STAGES = [
  "Uploading your file…",
  "Extracting charts…",
  "Matching fields to your data…",
  "Almost there — building the preview…",
];

function useUploadStage(active) {
  const [stage, setStage] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => {
    if (!active) { setStage(0); if (timerRef.current) clearInterval(timerRef.current); return; }
    setStage(0);
    timerRef.current = setInterval(() => {
      setStage(s => Math.min(s + 1, UPLOAD_STAGES.length - 1));
    }, 4000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [active]);
  return stage;
}

// Real ceiling, matching backend/app/routers/blueprint.py's _MAX_IMPORT_BYTES. The file uploads
// directly to S3 via a presigned PUT (POST /blueprint/import/presign) instead of being
// base64-encoded into /blueprint/import's JSON body — that removes API Gateway's non-adjustable
// 10MB payload limit from this path (base64 + JSON overhead would have capped the real file at
// ~7MB, well under the OLD 25MB this UI advertised — a file in that gap would have been rejected
// by API Gateway itself with a raw 413, never reaching a friendly error message). The actual
// request now runs in BlueprintImportContext (App-level, survives navigation) — see that file's
// header comment for why.
const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
};

const modalOverlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modalCard = { width: "min(720px,94vw)", maxHeight: "88vh", overflow: "auto", background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,.3)" };
const btnPrimary = { fontSize: 13, fontWeight: 700, padding: "9px 16px", borderRadius: 8, border: "none", background: "#4f46e5", color: "#fff", cursor: "pointer" };
const btnSecondary = { fontSize: 13, fontWeight: 600, padding: "9px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#334155", cursor: "pointer" };

// Each pill is how sure the system is that ONE field (the chart's category, or one of its
// series) was matched to the right column in your data — NOT a rating of the chart's data
// quality or importance. Reported as confusing live ("what does High/Low/Medium mean here?"),
// so every pill now carries a specific tooltip explaining what confidence means for THAT field.
const CONFIDENCE_EXPLAIN = {
  high: "High confidence — this field was matched with strong certainty (an exact prior confirmation, or a clear name match to a column in your data).",
  medium: "Medium confidence — this field was matched, but the name similarity to a column in your data was only moderate. Worth a quick check.",
  low: "Low confidence — the system couldn't find a clear match for this field in your data. Double-check this one before publishing.",
};

function ConfidencePill({ confidence }) {
  const map = { high: ["#dcfce7", "#166534"], medium: ["#fef3c7", "#92400e"], low: ["#fee2e2", "#991b1b"] };
  const c = confidence || "low";
  const [bg, fg] = map[c] || map.low;
  return <span title={CONFIDENCE_EXPLAIN[c] || CONFIDENCE_EXPLAIN.low} style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".03em", background: bg, color: fg, borderRadius: 999, padding: "2px 8px", cursor: "help" }}>{c}</span>;
}

// A pill row of unlabeled HIGH/LOW/MEDIUM badges reads as an unexplained wall of colors — a
// customer can't tell WHICH field each pill rates ("what does the label per chart mean?",
// reported live). Each row here pairs the pill with the actual field it's rating — the chart's
// breakdown ("Category (breakdown)") or the specific series label from the original report
// (e.g. "25%", "Impresiones") — so it reads as a short checklist instead of a color wall.
function LabeledConfidenceList({ items, fallback }) {
  // `items` (labeledConfidences) is the normal path; `fallback` (the old flat `confidences`
  // array) only applies if a backend response somehow omits labels — keeps this component from
  // rendering nothing for an older/unexpected payload shape.
  const rows = (items && items.length > 0)
    ? items
    : (fallback || []).map((c, i) => ({ label: `Field ${i + 1}`, confidence: c }));
  if (rows.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 6 }}>
      {rows.map((it, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "#475569" }}>
          <ConfidencePill confidence={it.confidence} />
          <span>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// REAL UX complaint, reported live: "⚠ Values differ from original" read as an alarming,
// unexplained warning — a customer testing this had NOT changed their data and had refreshed
// the dashboard several times, and still saw it, with no way to tell why. The underlying
// comparison (this chart's LIVE computed number vs. a STATIC snapshot cached inside the
// template file the last time it was saved in Excel/PowerPoint) isn't something a customer can
// act on either way: a genuine difference is expected and correct once live data has moved on
// from that snapshot, and there's no way from a bare badge to tell "your data changed" apart
// from "something's actually wrong with the mapping" — the SAME confusion applied to the muted
// "No original values to compare" state for a chart with no snapshot at all, just with scarier
// styling. Per that feedback, ONLY the positive, unambiguous signal is shown now (this chart's
// live number matches its saved snapshot) — a mismatch or a missing snapshot renders NOTHING,
// rather than a badge implying a problem the customer usually can't act on. The underlying
// comparison still runs and still gates the one-click "Everything matches — publish" fast path
// (`allAutoVerified`/`auto_verified`, `blueprint_reconcile.auto_verify`) — this change is
// display-only.
function ReconcileBadge({ status }) {
  if (status === "verified") return <span style={{ fontSize: 11, fontWeight: 700, color: "#166534" }}>✓ Matches original</span>;
  return null;
}

// Renders the ACTUAL graph the wizard is about to publish — the same deterministic chart_spec/
// result_set the Dashboard renders once live (`_side_by_side_payload`'s `preview`, computed
// against real project data), reusing AutoDeck's own ChartView rather than a second renderer.
// Real gap reported live: the confirm screen only ever showed confidence pills and text badges —
// no way to see what a chart will actually LOOK like before committing to Publish.
function VisualPreview({ preview }) {
  if (!preview || preview.status !== "ready") {
    return (
      <div style={{ padding: "18px 0", textAlign: "center", fontSize: 11.5, color: "#94a3b8" }}>
        No preview available for this chart yet.
      </div>
    );
  }
  const spec = preview.chart_spec || {};
  if (spec.type === "stat") {
    return (
      <div style={{ padding: "14px 0", textAlign: "center" }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a" }}>{fmtValue(spec.value, spec.format)}</div>
      </div>
    );
  }
  return <ChartView visual={preview} palette={null} roles={null} />;
}

// ── Tables step (blueprint-tables-and-concepts, Tasks 21/22) ────────────────────────────────
// Sits between chart "confirm" and "publish". A candidate is a native PivotTable
// (`tableCandidates`, from `list_pivot_candidates` at import time) the customer can check off, or
// a manually-typed range ("+ Add manual range") for a hand-built summary grid with no pivot
// behind it. Either path converges on the SAME confirm→resolve flow: `POST /blueprint/tables/
// validate-range` (manual only) → `POST /blueprint/tables/resolve` → a resolved table with its
// own cells, shape, and publish eligibility, appended to `pinnedTables`.
//
// Most reference files have nothing worth pinning as a table — an explicit, always-visible
// "Skip" path keeps this step from being a mandatory extra click for every import just because
// prepare_for_tables is now requested by default (BlueprintImportContext.jsx).
const RESOLVE_STAGES = ["Reading your range…", "Matching to your data…"];

function CandidateCard({ candidate, checked, onToggle, resolving }) {
  return (
    <label style={{
      display: "flex", alignItems: "flex-start", gap: 10, border: "1px solid #e2e8f0", borderRadius: 10,
      padding: "10px 12px", cursor: resolving ? "default" : "pointer", background: checked ? "#eef2ff" : "#fff",
    }}>
      <input type="checkbox" checked={checked} disabled={resolving} onChange={onToggle} style={{ marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{candidate.sheet} — {candidate.range}</div>
        {candidate.preview_labels && candidate.preview_labels.length > 0 && (
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
            {candidate.preview_labels.slice(0, 3).join(", ")}
          </div>
        )}
      </div>
    </label>
  );
}

// UX redesign (reported live: "too long, not visual friendly, I need to scroll down a lot"):
// group unresolved cells by their identical `raw_label` before rendering. This is not JUST a
// visual grouping — a Concept is confirmed by NAME (`concepts.resolve_concept`, project-then-
// tenant, case-insensitive), so every unresolved cell sharing the SAME label resolves from the
// exact same one Confirm action (`reresolveAll` re-runs resolve for the whole table afterward).
// A table with, say, 12 cells all showing the unmatched word "Investment" previously rendered
// 12 near-identical explanation cards; now it renders ONE group asking about "Investment" once,
// with a count badge — answering it clears every cell in the group in one step. A cell with no
// readable `raw_label` (rare — a formula whose displayed text couldn't be read) gets its own
// singleton group keyed by position, since there's no shared name to group it under.
// REAL BUG, found live-testing the actual KIWI file: a cell unresolved because its SOURCE
// COLUMN isn't mapped (e.g. "'Engagement Ro2' does not match any known column.") has NO
// `raw_label` at all — a formula cell's only "displayed text" is its own cached NUMERIC
// result (correctly suppressed, see `table_pins._unresolved_cell`'s own comment: a number is
// never a nameable "word" to explain). Every such cell fell into its own per-POSITION singleton
// group with no name at all, rendering as a useless generic "This cell" header — reported live:
// "I tried to explain but the analyze doesn't understand me" (there is genuinely nothing to
// type an explanation FOR; the reason already names the real missing column). Fix: when
// `raw_label` is empty, extract the column name the backend's OWN reason string already quotes
// (`'X' does not match any known column`/`'X'/'Y' don't match any known columns`) via
// `_extractReasonLabel` — used BOTH to group identical missing-column reasons together (was
// separate singleton groups before) AND to show the real name instead of "This cell". Grouping
// key ONLY (never sent to the backend as a Concept name) — `ClarifyCell` still reads `cell.
// raw_label` directly for its own Confirm flow, unaffected by this display-only change.
// #184 (customer WoW Media template, Dev): that grouping still produced a bare "This cell" for
// every label-less cell (e.g. the 10 cells of a pivot's "(blank)" row). Grouping and labelling
// now live in `tableIssues.js` (backend `issues` payload first, rebuilt from cells otherwise):
// every issue names sheet!cell, row label, column header and value — never "This cell".

// #188 "Does this table match your Excel?" — the result of POST /blueprint/tables/reconcile
// (the SAME calculation the dashboard runs, compared with the values saved in the Excel file).
// A difference never hard-blocks: the customer reviews the grouped cells and confirms (that's
// recorded on the pinned table). A failed check is said out loud and needs the same confirmation
// (or "Check again"). The live region is always mounted so screen readers hear each change.
const RECONCILE_CELL_PREVIEW = 5;
function ReconcilePanel({ table, onAck, onRecheck }) {
  const [showAll, setShowAll] = useState(false);
  const [openUncalc, setOpenUncalc] = useState(() => new Set());   // uncalculable groups showing their cells
  const gate = reconcileGate(table);
  const r = table.reconcile || null;
  let body = null;
  if (gate.state === "checking") {
    body = (
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#4f46e5" }}>
        <RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Checking these numbers against your Excel…
      </div>
    );
  } else if (r && gate.state === "ok") {
    body = r.status === "verified" ? (
      <div style={{ fontSize: 11.5, color: "#166534", fontWeight: 600 }}>✓ Matches your Excel ({r.checked} values checked)</div>
    ) : null;
  } else if (r) {
    const isError = r.status === "error";
    const groups = r.groups || [];
    // fix-source-routing (F2): cells that differ (`kind: "mismatch"`, a short preview) and cells
    // that couldn't be calculated (`kind: "uncalculable"`, one sentence per cause; the cell list
    // only on request) are listed separately — never one lump.
    const mismatchGroups = groups.filter((g) => g.kind !== "uncalculable");
    const uncalcGroups = groups.filter((g) => g.kind === "uncalculable");
    const listed = mismatchGroups.reduce((n, g) => n + (g.cells || []).length, 0);
    let budget = showAll ? Infinity : RECONCILE_CELL_PREVIEW;
    const visibleGroups = mismatchGroups.map((g) => {
      const cells = (g.cells || []).slice(0, Math.max(0, budget));
      budget -= cells.length;
      return { ...g, shownCells: cells };
    }).filter((g) => g.shownCells.length > 0);
    const groupKey = (g, i) => `${g.kind || "mismatch"}|${g.column_header || ""}|${g.cause || ""}|${i}`;
    const toggleUncalc = (key) => setOpenUncalc((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    body = (
      <div style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 12, color: "#92400e", fontWeight: 700 }}>⚠ {r.headline}</div>
        {r.reason_text && <div style={{ fontSize: 11.5, color: "#92400e", lineHeight: 1.45 }}>{r.reason_text}</div>}
        {visibleGroups.map((g, i) => (
          <div key={groupKey(g, i)}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: "#78350f" }}>{reconcileGroupTitle(g)}</div>
            <ul style={{ margin: "2px 0 0", paddingLeft: 18, fontSize: 11.5, color: "#78350f" }}>
              {g.shownCells.map((c) => <li key={c.sheet_address}>{reconcileCellLine(c)}</li>)}
            </ul>
          </div>
        ))}
        {listed > RECONCILE_CELL_PREVIEW && (
          <button type="button" onClick={() => setShowAll((v) => !v)} style={{ alignSelf: "flex-start", fontSize: 11, color: "#b45309", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>
            {showAll ? "Show fewer" : `Show all ${listed} listed cells`}
          </button>
        )}
        {uncalcGroups.map((g, i) => {
          const key = groupKey(g, i);
          const cells = g.cells || [];
          const open = openUncalc.has(key);
          return (
            <div key={key}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "#78350f", lineHeight: 1.45 }}>{reconcileGroupTitle(g)}</div>
              {g.action_text && <div style={{ fontSize: 11.5, color: "#92400e", lineHeight: 1.45 }}><b>What to do:</b> {g.action_text}</div>}
              {cells.length > 0 && (
                <button type="button" aria-expanded={open} onClick={() => toggleUncalc(key)} style={{ fontSize: 11, color: "#b45309", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>
                  {open ? "Hide cells" : `Show ${cells.length} ${cells.length === 1 ? "cell" : "cells"}`}
                </button>
              )}
              {open && (
                <ul style={{ margin: "2px 0 0", paddingLeft: 18, fontSize: 11.5, color: "#78350f" }}>
                  {cells.map((c) => <li key={c.sheet_address}>{reconcileCellLine(c)}</li>)}
                </ul>
              )}
            </div>
          );
        })}
        {r.nudge_text && <div style={{ fontSize: 11.5, color: "#92400e", lineHeight: 1.45 }}>{r.nudge_text}</div>}
        {r.action_text && <div style={{ fontSize: 11.5, color: "#92400e" }}><b>What to do:</b> {r.action_text}</div>}
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#78350f", cursor: "pointer" }}>
          <input type="checkbox" checked={table.reconcile_ack === true} onChange={(e) => onAck(e.target.checked)} style={{ marginTop: 2 }} />
          <span>{r.confirm_label}</span>
        </label>
        {isError && (
          <button type="button" onClick={onRecheck} style={{ ...btnSecondary, alignSelf: "flex-start", padding: "5px 11px", fontSize: 12 }}>
            Check again
          </button>
        )}
      </div>
    );
  }
  return <div role="status" aria-live="polite" style={{ marginTop: body ? 8 : 0 }}>{body}</div>;
}

// One resolved Pinned Table's summary card — a Grid/Stat-Card preview isn't rendered here (that's
// the Dashboard's job once published); this just confirms what was found and surfaces any
// unresolved cell inline for clarification (Task 22), never blocking the OTHER cards.
// #188: plus the Excel pivot filters it applies (read-only chips) and the "matches your Excel?"
// check with its confirm step.
function ResolvedTableCard({ table, onRemove, onConceptConfirmed, call, onReconcileAck, onRecheck, onChooseSource, choosingSource }) {
  // fix-source-routing: which ONE data source this table reads (always shown), the dropdown only
  // when the backend couldn't decide, and a status line that waits for the Excel check.
  const srcLabel = sourceLabelFor(table);
  const srcChoice = sourceChoiceState(table);
  const statusLine = tableStatusLine(table);
  const selectId = `source-choice-${table.table_id}`;
  const unresolvedCells = (table.cells || []).filter(c => c.kind === "unresolved");
  const groups = issuesForTable(table).map(g => ({ ...g, key: g.issue_key }));
  const notes = notesForTable(table);
  // A short list (a couple of distinct things to clarify) stays open by default — no extra
  // click needed for the common case. A longer one (the actual "too long to scroll" complaint)
  // defaults collapsed to a scannable checklist; expand only what you're ready to answer.
  const [expanded, setExpanded] = useState(() => new Set(groups.length <= 2 ? groups.map(g => g.key) : []));
  const toggleGroup = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {table.sheet} — {table.range}
            {/* Requirement 8 carry-forward — distinguishes a table already on the dashboard
                (re-resolved fresh against THIS upload, nothing to re-pick) from one just found/
                added this session, so it's clear why it showed up with no action taken. */}
            {table.source === "carried_over" && (
              <span title="This table is already on your dashboard — its data was refreshed from this upload. Remove it below if you no longer want it." style={{ fontSize: 10, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", border: "1px solid #bae6fd", borderRadius: 999, padding: "1px 8px", textTransform: "none", letterSpacing: "normal" }}>
                Already on your dashboard
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {table.shape === "grid" ? "Grid table" : "Stat-card table"} · {(table.cells || []).length} cell{(table.cells || []).length === 1 ? "" : "s"}
          </div>
          {srcLabel && (
            <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{srcLabel}</div>
          )}
          <TableFilterChips table={table} />
          {table.tableFiltersError && (
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              We couldn't list this table's Excel filters ({table.tableFiltersError}). Its numbers still use them.
            </div>
          )}
        </div>
        <button onClick={onRemove} style={{ fontSize: 11.5, fontWeight: 600, padding: "5px 10px", borderRadius: 999, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", cursor: "pointer", whiteSpace: "nowrap" }}>
          Remove
        </button>
      </div>
      {/* #184 non-blocking data notes (e.g. Excel's "(blank)" pivot row): never hidden, never a blocker. */}
      {notes.map((n) => (
        <div key={`${n.code}-${n.row}`} role="note" style={{ fontSize: 11.5, color: "#1e3a8a", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, padding: "6px 9px", marginTop: 8, lineHeight: 1.45 }}>
          ⓘ {n.text}
        </div>
      ))}
      {/* fix-source-routing: offered ONLY when the backend couldn't tell which data source this
          table uses (never a guess, never a silent fallback); the options come from the backend. */}
      {srcChoice.required && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          <label htmlFor={selectId} style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Which data does this table use?</label>
          <select id={selectId} value={srcChoice.chosen} disabled={!!choosingSource}
            onChange={(e) => { if (e.target.value && onChooseSource) onChooseSource(e.target.value); }}
            style={{ fontSize: 12.5, padding: "7px 9px", borderRadius: 7, border: "1px solid #cbd5e1", background: "#fff", maxWidth: 460 }}>
            <option value="" disabled>Select a data source…</option>
            {srcChoice.options.map((o) => <option key={o.category} value={o.category}>{o.text}</option>)}
          </select>
        </div>
      )}
      {table.canPublish ? (
        <>
          <div style={{ fontSize: 11.5, color: statusLine.tone === "ok" ? "#166534" : "#b45309", marginTop: 8, fontWeight: 600 }}>{statusLine.text}</div>
          <ReconcilePanel table={table} onAck={onReconcileAck} onRecheck={onRecheck} />
        </>
      ) : (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}>
            ⚠ {tableSummary(table) || "Some cells in this table need attention before it can publish."}
            {unresolvedCells.length > groups.length && ` (${unresolvedCells.length} cells total)`}
          </div>
          {groups.map((g) => {
            const isOpen = expanded.has(g.key);
            const count = g.cells.length;
            return (
              <div key={g.key} style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, overflow: "hidden" }}>
                <button onClick={() => toggleGroup(g.key)} style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "9px 12px", background: "none", border: "none", cursor: "pointer", textAlign: "left",
                }}>
                  <span style={{ fontSize: 12, color: "#92400e" }}>
                    <b>{g.headline}</b>{count > 1 && (
                      <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: "#92400e", background: "#fde68a", borderRadius: 999, padding: "1px 7px" }}>
                        {count} cells
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 11, color: "#b45309" }}>{isOpen ? "Hide" : (g.explainable ? "Explain" : "Details")}</span>
                </button>
                {isOpen && (
                  <div style={{ padding: "0 12px 10px" }}>
                    <ClarifyCell issue={g} call={call} onConfirmed={onConceptConfirmed} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Inline clarification for ONE unresolved cell (Task 22, R5.1/R5.2): the customer explains what
// the word/cell means in their own words -> derive_concept_formula proposes a DSL -> the customer
// REVIEWS/edits it before confirming (never an auto-confirmed chain — the proposal is a guess,
// confirm_concept's own collision/compile checks are the real gate). On confirm, re-runs resolve
// for the whole table (customer_id/project_id already flow through resolve_table_range server-
// side, so a cell matching this newly-confirmed Concept now resolves without a re-upload).
// #184: driven by one grouped ISSUE (see tableIssues.js). Explain is offered ONLY when the issue
// has a real word to name a Concept by (`explainable`/`explain_label`) — before, a label-less cell
// sent `key: null` and always failed with a 422 ("Couldn't save this…"). Other issues show the
// exact cells and a concrete action instead.
const CELL_PREVIEW = 5;
function ClarifyCell({ issue, call, onConfirmed }) {
  const [showAll, setShowAll] = useState(false);
  const cells = issue.cells || [];
  const extraCount = cells.length - 1;
  const [explanation, setExplanation] = useState("");
  const [deriving, setDeriving] = useState(false);
  const [proposal, setProposal] = useState(null);   // {dsl, reason} once derived
  const [dslDraft, setDslDraft] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState("");

  const derive = async () => {
    if (!explanation.trim()) return;
    setDeriving(true); setErr(""); setProposal(null);
    try {
      const d = await call("/blueprint/concepts/derive", "POST", { explanation });
      if (!d.dsl) { setErr(d.reason || "Couldn't derive a formula from that explanation — try rephrasing."); return; }
      setProposal(d);
      setDslDraft(d.dsl);
    } catch (e) {
      setErr("Something went wrong deriving a formula. Please try again.");
    } finally {
      setDeriving(false);
    }
  };

  const confirm = async () => {
    if (!dslDraft.trim()) return;
    setConfirming(true); setErr("");
    try {
      await call("/blueprint/concepts", "POST", {
        key: issue.explain_label, label: issue.explain_label, explanation, dsl: dslDraft, scope: "project",
      });
      onConfirmed();
    } catch (e) {
      setErr("Couldn't save this — the name may collide with a real column, or the formula isn't valid. Try a different name or formula.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 12, color: "#92400e" }}>
        {issue.reason_text}
        {issue.explainable && extraCount > 0 && ` This answer also applies to the other ${extraCount} cell${extraCount === 1 ? "" : "s"} listed below.`}
      </div>
      <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11.5, color: "#78350f" }}>
        {(showAll ? cells : cells.slice(0, CELL_PREVIEW)).map((c) => (
          <li key={c.sheet_address || c.address}>{issueCellLine(c)}</li>
        ))}
      </ul>
      {cells.length > CELL_PREVIEW && (
        <button onClick={() => setShowAll(v => !v)} style={{ fontSize: 11, color: "#b45309", background: "none", border: "none", padding: "2px 0", cursor: "pointer", textDecoration: "underline" }}>
          {showAll ? "Show fewer" : `Show all ${cells.length} cells`}
        </button>
      )}
      {issue.action_text && (
        <div style={{ fontSize: 11.5, color: "#92400e", marginTop: 6 }}><b>What to do:</b> {issue.action_text}</div>
      )}
      {!issue.explainable ? null : !proposal ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input value={explanation} onChange={e => setExplanation(e.target.value)}
            aria-label={`Explain what "${issue.explain_label}" means`}
            placeholder={issue.explain_placeholder}
            disabled={deriving} style={{ flex: 1, fontSize: 12, padding: "7px 9px", borderRadius: 7, border: "1px solid #fcd34d" }} />
          <button onClick={derive} disabled={deriving || !explanation.trim()} style={{ ...btnPrimary, padding: "7px 12px", fontSize: 12, opacity: (deriving || !explanation.trim()) ? 0.6 : 1 }}>
            {deriving ? "Thinking…" : "Explain"}
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Proposed formula — review or edit before confirming:</div>
          <input value={dslDraft} onChange={e => setDslDraft(e.target.value)} disabled={confirming}
            style={{ width: "100%", fontSize: 12, fontFamily: "monospace", padding: "7px 9px", borderRadius: 7, border: "1px solid #fcd34d", marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setProposal(null); setExplanation(""); }} disabled={confirming} style={{ ...btnSecondary, padding: "6px 11px", fontSize: 12 }}>
              Try again
            </button>
            <button onClick={confirm} disabled={confirming || !dslDraft.trim()} style={{ ...btnPrimary, padding: "6px 11px", fontSize: 12, opacity: (confirming || !dslDraft.trim()) ? 0.6 : 1 }}>
              {confirming ? "Confirming…" : "Confirm"}
            </button>
          </div>
        </div>
      )}
      {err && <div style={{ fontSize: 11.5, color: "#991b1b", marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function TablesStep({ call, noChartsNotice = false, tablesScratchKey, tableCandidates, pinnedTables, setPinnedTables,
                     sheetNames, slideCount, staleTableNotices = [], onBack, onSkip, onContinue }) {
  const [checked, setChecked] = useState(() => new Set());
  const [resolving, setResolving] = useState(false);
  const [resolveErr, setResolveErr] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSheet, setManualSheet] = useState("");
  const [manualStart, setManualStart] = useState("");
  const [manualEnd, setManualEnd] = useState("");
  const [manualErr, setManualErr] = useState("");
  const [stage, setStage] = useState(0);
  const stageRef = useRef(null);

  const startResolveStage = () => {
    setStage(0);
    stageRef.current = setInterval(() => setStage(s => Math.min(s + 1, RESOLVE_STAGES.length - 1)), 1500);
  };
  const stopResolveStage = () => { if (stageRef.current) clearInterval(stageRef.current); stageRef.current = null; };
  useEffect(() => () => stopResolveStage(), []);

  const alreadyPinned = (sheet, range) => pinnedTables.some(t => t.sheet === sheet && t.range === range);

  const resolveRange = async (sheet, range, source) => {
    if (!tablesScratchKey) { setResolveErr("This file's data isn't available anymore — please re-upload to add a table."); return; }
    setResolving(true); setResolveErr(""); startResolveStage();
    try {
      const d = await call("/blueprint/tables/resolve", "POST", { tables_scratch_key: tablesScratchKey, sheet, range });
      const tableId = `table_${pinnedTables.length + 1}_${Date.now()}`;
      setPinnedTables(prev => [...prev, {
        table_id: tableId, sheet: d.sheet, range: d.range, shape: d.shape, cells: d.cells,
        anchor_label: d.anchorLabel, source, canPublish: d.canPublish, blockingCells: d.blockingCells,
        issues: d.issues, summary: d.summary, notes: d.notes,
        // #188: the Excel pivot's filters (chips) + #187 seed; the reconcile check follows (effect below)
        table_filters: d.tableFilters || [], pivot_definition: d.pivotDefinition || null,
        tableFiltersError: d.tableFiltersError || null,
        // fix-source-routing: which data source this table reads (label + status + options)
        ...sourceFieldsFrom(d, null),
      }]);
    } catch (e) {
      setResolveErr("Couldn't read that range from your file. Please try again.");
    } finally {
      setResolving(false); stopResolveStage();
    }
  };

  const toggleCandidate = async (candidate) => {
    const key = `${candidate.sheet}::${candidate.range}`;
    const next = new Set(checked);
    if (next.has(key)) {
      next.delete(key);
      setChecked(next);
      setPinnedTables(prev => prev.filter(t => !(t.sheet === candidate.sheet && t.range === candidate.range)));
      return;
    }
    next.add(key);
    setChecked(next);
    if (!alreadyPinned(candidate.sheet, candidate.range)) {
      await resolveRange(candidate.sheet, candidate.range, "pivot");
    }
  };

  const submitManualRange = async () => {
    if (!tablesScratchKey) { setManualErr("This file's data isn't available anymore — please re-upload to add a table."); return; }
    if (!manualSheet.trim() || !manualStart.trim() || !manualEnd.trim()) return;
    setManualErr("");
    try {
      const v = await call("/blueprint/tables/validate-range", "POST", {
        tables_scratch_key: tablesScratchKey, sheet: manualSheet.trim(),
        start_cell: manualStart.trim(), end_cell: manualEnd.trim(),
      });
      // Block adding the same table twice (#3): check the BACKEND-normalized {sheet, range}
      // (e.g. "Sheet1"/"B10:E15") rather than the raw typed text — the same range can be typed
      // with different casing/whitespace/$-signs and still normalize to an identical pin, so
      // comparing post-validation avoids a false negative that would let an obvious duplicate
      // (retyped verbatim) slip through as if it were a different table.
      if (alreadyPinned(v.sheet, v.range)) {
        setManualErr(`"${v.sheet}" ${v.range} is already added below.`);
        return;
      }
      setManualOpen(false); setManualSheet(""); setManualStart(""); setManualEnd("");
      await resolveRange(v.sheet, v.range, "manual");
    } catch (e) {
      setManualErr("That range couldn't be validated — check the sheet name and cell references.");
    }
  };

  const removeTable = (tableId) => {
    setPinnedTables(prev => prev.filter(t => t.table_id !== tableId));
    setChecked(prev => {
      const next = new Set(prev);
      for (const t of pinnedTables) if (t.table_id === tableId) next.delete(`${t.sheet}::${t.range}`);
      return next;
    });
  };

  // fix-source-routing: the source fields a resolve response carries. A re-resolve with the
  // customer's choice returns no options (nothing left to decide), so the earlier options are
  // kept for the dropdown — the customer can still change their pick.
  const sourceFieldsFrom = (d, prev) => ({
    sourceCategory: d.sourceCategory || null,
    sourceLabel: d.sourceLabel || null,
    sourceStatus: d.sourceStatus || null,
    sourceOptions: (d.sourceOptions && d.sourceOptions.length) ? d.sourceOptions
      : ((prev && (prev.source_choice || prev.sourceStatus === "chosen") && prev.sourceOptions) || []),
  });
  // The category a re-resolve must keep: the customer's pick this session, or a carried-over
  // table's stored choice. Never a guess.
  const keptChoice = (t) => t.source_choice
    || (t.sourceStatus === "chosen" && t.sourceCategory && t.sourceCategory.category) || null;

  // "Which data does this table use?" — re-resolve THIS table against the chosen source (same
  // table_id); the previous Excel check no longer applies, so it resets exactly like reresolveAll.
  const [choosingSourceFor, setChoosingSourceFor] = useState(null);
  const chooseSource = async (tableId, category) => {
    const t = pinnedTables.find(x => x.table_id === tableId);
    if (!t || !tablesScratchKey) return;
    setChoosingSourceFor(tableId); setResolveErr("");
    try {
      const d = await call("/blueprint/tables/resolve", "POST", {
        tables_scratch_key: tablesScratchKey, sheet: t.sheet, range: t.range, source_category: category,
      });
      setPinnedTables(prev => prev.map(x => (x.table_id !== tableId ? x : {
        ...x, cells: d.cells, shape: d.shape, canPublish: d.canPublish, blockingCells: d.blockingCells,
        issues: d.issues, summary: d.summary, notes: d.notes,
        table_filters: d.tableFilters || [], pivot_definition: d.pivotDefinition || null,
        tableFiltersError: d.tableFiltersError || null,
        source_choice: category, ...sourceFieldsFrom(d, { ...x, source_choice: category }),
        reconcile: undefined, reconcile_ack: false, reconcileChecking: false, reconcileToken: null,
      })));
    } catch (e) {
      const detail = e && e.body && typeof e.body.detail === "string" ? e.body.detail : null;
      setResolveErr(detail || "Couldn't read that range from your file. Please try again.");
    } finally {
      setChoosingSourceFor(null);
    }
  };

  // Re-resolve every pinned table after a new Concept is confirmed mid-session (Task 22/R5.3) —
  // a cell that was "unresolved" because the Concept didn't exist yet now matches it, with no
  // re-upload needed. Re-runs resolve for every currently-pinned table's own {sheet, range}.
  const reresolveAll = async () => {
    setResolving(true);
    try {
      const updated = await Promise.all(pinnedTables.map(async (t) => {
        try {
          const choice = keptChoice(t);
          const body = { tables_scratch_key: tablesScratchKey, sheet: t.sheet, range: t.range };
          if (choice) body.source_category = choice;   // fix-source-routing: the pick persists
          const d = await call("/blueprint/tables/resolve", "POST", body);
          // #188: fresh cells -> fresh filters, and the previous check/confirmation no longer
          // applies (reconcile reset to undefined re-runs the check via the effect below).
          return { ...t, cells: d.cells, shape: d.shape, canPublish: d.canPublish, blockingCells: d.blockingCells,
                   issues: d.issues, summary: d.summary, notes: d.notes,
                   table_filters: d.tableFilters || [], pivot_definition: d.pivotDefinition || null,
                   tableFiltersError: d.tableFiltersError || null,
                   ...sourceFieldsFrom(d, t),
                   reconcile: undefined, reconcile_ack: false, reconcileChecking: false, reconcileToken: null };
        } catch { return t; }
      }));
      setPinnedTables(updated);
    } finally {
      setResolving(false);
    }
  };

  // #188 — "does this table match your Excel?" A follow-up to resolve, never part of it (resolve
  // is already close to API Gateway's 30s cap). ONE effect covers every path a table arrives by:
  // a newly pinned table, a re-resolve (reresolveAll resets `reconcile`), a carried-over table
  // from a re-import, and "Check again". A token guards against an older check landing on a
  // table whose cells changed meanwhile.
  const runReconcile = useCallback(async (t, token) => {
    const apply = (patch) => setPinnedTables(prev => prev.map(x =>
      (x.table_id === t.table_id && x.reconcileToken === token) ? { ...x, ...patch, reconcileChecking: false } : x));
    try {
      const d = await call("/blueprint/tables/reconcile", "POST", {
        sheet: t.sheet, range: t.range, shape: t.shape || null, cells: t.cells || [],
        table_filters: t.table_filters || [],
        source_category: t.sourceCategory || t.source_category || null,
      });
      // feature off -> null (not undefined) so the check isn't retried in a loop
      apply({ reconcile: (d && d.enabled !== false && d.reconcile) || null, reconcile_ack: false });
    } catch (e) {
      apply({ reconcile: reconcileRequestFailed(t), reconcile_ack: false });
    }
  }, [call, setPinnedTables]);

  useEffect(() => {
    const todo = pinnedTables.filter(t => t.canPublish && t.reconcile === undefined && !t.reconcileChecking);
    if (!todo.length) return;
    const tokens = {};
    todo.forEach((t, i) => { tokens[t.table_id] = `${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`; });
    setPinnedTables(prev => prev.map(t => (tokens[t.table_id] ? { ...t, reconcileChecking: true, reconcileToken: tokens[t.table_id] } : t)));
    todo.forEach(t => { runReconcile(t, tokens[t.table_id]); });
  }, [pinnedTables, runReconcile, setPinnedTables]);

  const setReconcileAck = (tableId, checked) =>
    setPinnedTables(prev => prev.map(t => (t.table_id === tableId ? { ...t, reconcile_ack: checked } : t)));
  const recheckTable = (tableId) =>
    setPinnedTables(prev => prev.map(t => (t.table_id === tableId
      ? { ...t, reconcile: undefined, reconcile_ack: false, reconcileChecking: false, reconcileToken: null } : t)));

  const anyBlocked = pinnedTables.some(t => !t.canPublish);
  const reconcileState = tablesReconcileGate(pinnedTables);
  const continueDisabled = anyBlocked || !reconcileState.canPublish;
  const continueHint = anyBlocked ? "Resolve or remove the blocked table(s) above first"
    : reconcileState.checking ? "Checking your tables against your Excel…"
    : reconcileState.needsConfirm ? "Review the differences from your Excel above and confirm (or remove the table) to continue."
    : "";

  return (
    <>
      {/* roadmap #174: a gentle, non-blocking notice when the template had no native charts —
          the import still succeeded and the customer can pin its PivotTables / summary ranges
          here. Only shown for the chart-less case; a chart-bearing template never sees it. */}
      {noChartsNotice && (
        <div style={{ marginBottom: 14, fontSize: 12.5, color: "#1e3a8a", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 12px", lineHeight: 1.5 }}>
          <b>No charts found in this file</b> — that's okay. We couldn't find any native charts to
          match, but you can still pin the summary tables below (we detected your PivotTables and
          sheets) and publish them to your dashboard.
        </div>
      )}
      <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 16px", lineHeight: 1.5 }}>
        {noChartsNotice
          ? "Pick the KPI or summary tables you want on your dashboard — choose a detected table below, or add a specific cell range from any sheet."
          : "If your report has any KPI or summary tables (not just charts), you can add them here too — they'll appear on your dashboard alongside your charts. This step is optional."}
      </p>

      {tableCandidates.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".03em", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span>Found in your file</span>
            {tableCandidates.length > 6 && (
              <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: "normal", color: "#94a3b8", fontSize: 11 }}>
                {tableCandidates.length} pivot tables detected — scroll to see all
              </span>
            )}
          </div>
          {/* Real UX bug, reported live: a reference file with many native PivotTables (e.g. one
              per chart/breakdown, a real shape) rendered this list with NO cap at all, growing
              the whole modal instead of scrolling internally — "too long... I need to scroll
              down a lot." A fixed max-height on just this list (not the modal) keeps the rest of
              the step (manual-range entry, pinned tables, Continue button) always in view. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflowY: "auto", paddingRight: 4 }}>
            {tableCandidates.map((c, i) => (
              <CandidateCard key={i} candidate={c} resolving={resolving}
                checked={checked.has(`${c.sheet}::${c.range}`)} onToggle={() => toggleCandidate(c)} />
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        {/* Table pinning only reads Excel workbooks server-side (table_pins._open_workbook
            rejects anything without an xl/ part) — a .pptx reference has no sheet grid to pin a
            manual range from, so offer this only for an Excel source rather than let a customer
            fill out a form that will always fail with "only supported for Excel". */}
        {slideCount > 0 ? (
          <div style={{ fontSize: 11.5, color: "#94a3b8", fontStyle: "italic" }}>
            Manual summary tables aren't available for PowerPoint references yet — charts extract normally above.
          </div>
        ) : !manualOpen ? (
          <button onClick={() => setManualOpen(true)} disabled={resolving} style={{ ...btnSecondary, opacity: resolving ? 0.6 : 1 }}>
            + Add manual range
          </button>
        ) : (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 11.5, color: "#64748b" }}>Pick the sheet and enter the cell range of a table you built yourself (not from a pivot):</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {/* Sheet picker (roadmap: "instead of always typing the Sheet name, can it be a
                  dropdown") — a <select> of the workbook's OWN sheet names (sheetNames, from
                  table_pins.list_sheet_names via /blueprint/import) so a customer picks a real
                  sheet instead of retyping its exact name/casing. Falls back to the original
                  free-text input only if the backend returned no sheet names at all (an
                  unreadable workbook.xml — table_pins.list_sheet_names degrades to [] rather
                  than raising, so this is the one legitimate case left needing free text). */}
              {sheetNames && sheetNames.length > 0 ? (
                <select value={manualSheet} onChange={e => setManualSheet(e.target.value)}
                  style={{ flex: 1, minWidth: 120, fontSize: 12.5, padding: "7px 9px", borderRadius: 7, border: "1px solid #cbd5e1", background: "#fff" }}>
                  <option value="">Select a sheet…</option>
                  {sheetNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              ) : (
                <input value={manualSheet} onChange={e => setManualSheet(e.target.value)} placeholder="Sheet name"
                  style={{ flex: 1, minWidth: 120, fontSize: 12.5, padding: "7px 9px", borderRadius: 7, border: "1px solid #cbd5e1" }} />
              )}
              <input value={manualStart} onChange={e => setManualStart(e.target.value)} placeholder="Start (e.g. B10)"
                style={{ width: 110, fontSize: 12.5, padding: "7px 9px", borderRadius: 7, border: "1px solid #cbd5e1" }} />
              <input value={manualEnd} onChange={e => setManualEnd(e.target.value)} placeholder="End (e.g. E15)"
                style={{ width: 110, fontSize: 12.5, padding: "7px 9px", borderRadius: 7, border: "1px solid #cbd5e1" }} />
            </div>
            {manualErr && <div style={{ fontSize: 11.5, color: "#991b1b" }}>{manualErr}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setManualOpen(false); setManualErr(""); }} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Cancel</button>
              <button onClick={submitManualRange} disabled={resolving} style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12, opacity: resolving ? 0.6 : 1 }}>Add</button>
            </div>
          </div>
        )}
      </div>

      {resolving && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 12, color: "#4f46e5" }}>
          <RefreshCw size={14} className="animate-spin" /> {RESOLVE_STAGES[stage]}
        </div>
      )}
      {resolveErr && (
        <div style={{ marginBottom: 14, fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}>{resolveErr}</div>
      )}

      {/* Requirement 8.3 — a table already on the dashboard whose anchor cell couldn't be
          found anywhere in THIS upload (never guessed at a replacement location). Its
          previously-published version keeps rendering on the dashboard unchanged; this just
          tells the customer it wasn't automatically carried into this publish, so they can
          re-add it (manual range/pivot pick) if it's still relevant to the new file. */}
      {staleTableNotices.length > 0 && (
        <div style={{ marginBottom: 14, fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px" }}>
          {staleTableNotices.length === 1 ? "⚠ One table" : `⚠ ${staleTableNotices.length} tables`} from your current dashboard couldn't be found in this file and will keep showing their last published data unless you re-add {staleTableNotices.length === 1 ? "it" : "them"} below:
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {staleTableNotices.map((s, i) => (
              <li key={i}>{s.anchor_label ? `"${s.anchor_label}"` : (s.table_id || "a table")}</li>
            ))}
          </ul>
        </div>
      )}

      {pinnedTables.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
          {pinnedTables.map((t) => (
            <ResolvedTableCard key={t.table_id} table={t} call={call}
              onRemove={() => removeTable(t.table_id)} onConceptConfirmed={reresolveAll}
              onReconcileAck={(checked) => setReconcileAck(t.table_id, checked)}
              onRecheck={() => recheckTable(t.table_id)}
              onChooseSource={(category) => chooseSource(t.table_id, category)}
              choosingSource={choosingSourceFor === t.table_id} />
          ))}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {/* roadmap #174: no "← Back" when this is a chart-less import (onBack is null) — there's
            no chart-review step to go back to; an empty span keeps the space-between layout. */}
        {onBack ? (
          <button onClick={onBack} style={btnSecondary}>← Back</button>
        ) : <span />}
        <div style={{ display: "flex", gap: 10 }}>
          {pinnedTables.length === 0 && (
            <button onClick={onSkip} style={btnSecondary}>Skip — I don't have any tables to add</button>
          )}
          <button onClick={onContinue} disabled={continueDisabled} title={continueHint}
            style={{ ...btnPrimary, opacity: continueDisabled ? 0.6 : 1 }}>
            Continue
          </button>
        </div>
      </div>
      {continueDisabled && continueHint && !anyBlocked && (
        <div role="status" style={{ fontSize: 11.5, color: "#64748b", marginTop: 8, textAlign: "right" }}>{continueHint}</div>
      )}
    </>
  );
}

export default function ImportWizard({ projectId, call, onClose, onPublished }) {
  const { startImport, clearImport, getImport } = useBlueprintImport();
  const active = getImport(projectId);   // survives this component unmounting/remounting entirely
  const [step, setStep] = useState(active?.status === "ready" ? "confirm" : "upload");
  // Blueprint Tables & Concepts (spec: blueprint-tables-and-concepts) — the "tables" step sits
  // between "confirm" (chart review) and Publish. `pinnedTables` = [{table_id, sheet, range,
  // shape, cells, anchor_label, source}, ...] — the SAME shape PublishRequest.tables already
  // accepts (Phase 5 wiring), so Publish sends chart Blueprint + Pinned Tables in ONE call.
  const [pinnedTables, setPinnedTables] = useState([]);
  const [rejectError, setRejectError] = useState("");   // client-side file-type rejection only
  const uploading = active?.status === "uploading";
  const uploadStage = useUploadStage(uploading);
  const importResult = active?.status === "ready" ? active.result : null;
  const tablesScratchKey = importResult?.tablesScratchKey || null;
  const tableCandidates = importResult?.tableCandidates || [];
  const staleTableNotices = importResult?.staleTableNotices || [];
  // Requirement 8 carry-forward (roadmap: "if I don't want to lose the current table, I need
  // to add it again... can we show the data of the table already in the dashboard so you don't
  // have to capture it again"). The backend re-locates + re-resolves every ALREADY-PUBLISHED
  // table against this new upload (`carriedOverTables`, pre-resolved against the file's CURRENT
  // data — never stale) — seed `pinnedTables` with them the moment a fresh import result
  // arrives, so re-editing a template no longer means re-picking every table from scratch. A
  // table the customer doesn't want anymore still just uses the existing "Remove" button
  // (identical to removing a freshly-added one — no new UI needed for that half of the flow).
  // Keyed on `importResult` itself (not `[]`) so a genuinely NEW upload (new active/result
  // object) re-seeds, but this effect never re-fires on every unrelated re-render.
  useEffect(() => {
    if (importResult?.carriedOverTables?.length) {
      setPinnedTables(prev => {
        const already = new Set(prev.map(t => t.table_id));
        const toAdd = importResult.carriedOverTables.filter(t => !already.has(t.table_id));
        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importResult]);
  const error = rejectError || (active?.status === "error" ? active.error : "");
  const [titleOverrides, setTitleOverrides] = useState({});  // {chartId: newTitle}
  const [excluded, setExcluded] = useState(() => new Set()); // chartIds dropped from publish
  const [publishing, setPublishing] = useState(false);
  const [publishErrors, setPublishErrors] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(() => new Set()); // chartIds with the chart preview expanded
  const togglePreview = (chartId) => {
    setPreviewOpen(prev => {
      const next = new Set(prev);
      if (next.has(chartId)) next.delete(chartId); else next.add(chartId);
      return next;
    });
  };

  // The upload/extract request itself now runs in BlueprintImportContext (App-level, mounted
  // above every page in App.jsx) — NOT here. This component can unmount at any point (navigate
  // away, close the modal) and the request keeps running to completion; reopening the wizard
  // (or the banner's persistent indicator, see AutoDeck.jsx) picks up wherever it left off via
  // `getImport(projectId)`. This is the same fix BoardActivityContext already proved out for the
  // board's "is it working" spinner, applied to imports after two live reports of a completed
  // extraction being silently thrown away by a stray click or a nav-away.
  useEffect(() => {
    if (active?.status !== "ready") return;
    // roadmap #174: a chart-less template skips the chart-review ("confirm") step entirely and
    // lands on the Tables step, where the "no charts found — you can still pin tables" notice
    // shows. Derived inline from the result (not the `noCharts` const below) so this effect has
    // no dependency on a value declared later in the component body.
    const r = active.result || {};
    const bpv = (r.blueprint && r.blueprint.visuals) || [];
    const resultNoCharts = r.noChartsNotice === true || r.hasCharts === false ||
      (bpv.length === 0 && (((r.tableCandidates || []).length > 0) || ((r.charts || []).length === 0)));
    setStep(resultNoCharts ? "tables" : "confirm");
  }, [active?.status, active?.result]);

  const doImport = useCallback((file) => {
    setRejectError("");
    if (file.size > MAX_BYTES) { setRejectError(`File exceeds the ${MAX_BYTES / (1024 * 1024)}MB limit.`); return; }
    setPinnedTables([]);   // a fresh upload has a fresh scratch key — any prior table pins are stale
    startImport(projectId, file);
  }, [projectId, startImport]);

  const onDrop = useCallback((accepted) => { if (accepted && accepted.length > 0) doImport(accepted[0]); }, [doImport]);
  // Without this, a file react-dropzone's OWN client-side accept/type check rejects (before
  // doImport ever runs) fails completely silently — no error, no network call, nothing. This
  // happened for real: a .xlsm can report an inconsistent/generic MIME type depending on
  // browser/OS, so the strict MIME match in ACCEPT below can reject a perfectly valid file with
  // no feedback. Surface it explicitly instead of leaving the user staring at an unresponsive
  // dropzone.
  const onDropRejected = useCallback((rejections) => {
    const first = rejections && rejections[0];
    const reason = first?.errors?.[0]?.message;
    setRejectError(reason || "That file wasn't accepted. Please upload a .xlsx, .xlsm, or .pptx file.");
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, onDropRejected, accept: ACCEPT, maxFiles: 1, disabled: uploading,
  });

  const toggleExclude = (chartId) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(chartId)) next.delete(chartId); else next.add(chartId);
      return next;
    });
  };

  // #188: a chart whose Excel filter can't be applied (`filter_warnings`) publishes only after an
  // explicit per-chart "publish without that filter" confirmation (server-enforced via
  // `unscoped_filters_ack` in validate_draft — this is the UI half).
  const [chartFilterAcks, setChartFilterAcks] = useState(() => new Set());
  const toggleChartFilterAck = (chartId) => {
    setChartFilterAcks(prev => {
      const next = new Set(prev);
      if (next.has(chartId)) next.delete(chartId); else next.add(chartId);
      return next;
    });
  };

  // Best-effort discard of the Task 20a Tables-scratch object — called on BOTH Publish and
  // Cancel/Close so the explicit-delete path (design's stated primary path) actually runs in the
  // common cases, rather than leaving the 1-day TTL sweep as the only cleanup that ever fires.
  // Never blocks/awaited-for-its-result by the caller's own flow — a discard failure here is not
  // the customer's problem, the sweep still catches it.
  const discardTablesScratch = useCallback(() => {
    if (!tablesScratchKey) return;
    call("/blueprint/tables/discard", "POST", { tables_scratch_key: tablesScratchKey }).catch(() => {});
  }, [tablesScratchKey, call]);

  const publish = async () => {
    if (!importResult) return;
    setPublishing(true); setPublishErrors([]);
    const finalBlueprint = {
      ...importResult.blueprint,
      visuals: (importResult.blueprint.visuals || [])
        .filter(v => !excluded.has(v.chart_id))
        .map(v => (titleOverrides[v.chart_id] ? { ...v, title: titleOverrides[v.chart_id] } : v))
        .map(v => (chartFilterAcks.has(v.chart_id) ? { ...v, unscoped_filters_ack: true } : v)),
    };
    try {
      // Pinned Tables publish in the SAME call as the chart Blueprint (Phase 5's independent
      // versioning happens server-side — this is just one customer-facing "Publish" click for
      // both, per the design's explicit goal of not making Tables feel like a second feature).
      // #188: each table carries its reconcile SUMMARY + confirmation (the server gate + the
      // stored `import_reconcile` stamp read these), never the full list of differing cells.
      const d = await call("/blueprint/publish", "POST", {
        dictionary: importResult.dictionary, blueprint: finalBlueprint, tables: pinnedTables.map(tableForPublish),
      });
      if (d && d.enabled === false) { setPublishErrors(["This feature isn't enabled for your account."]); return; }
      discardTablesScratch();
      clearImport(projectId);   // consumed — clear the persistent slot so the banner/indicator clears too
      onPublished();
    } catch (e) {
      // The router returns 400 {errors:[...]} on a failed validate_draft — call()'s Error message
      // is just the status code, so surface a generic message with a pointer to re-check the list
      // above; the per-visual list already shown in "confirm" is the best available detail.
      // #188: a table blocked by the server (e.g. an unconfirmed difference from Excel) comes
      // back with its own plain-language `reason` — show that instead of the generic line.
      const blocked = (((e && e.body && e.body.detail) || {}).tables || {}).blocked || [];
      // Same-cause blocks (e.g. "too large to save together") share one reason: show it once.
      const reasons = [...new Set(blocked.map(b => b && b.reason).filter(Boolean))];
      setPublishErrors(reasons.length ? reasons
        : ["Some visuals couldn't be published — remove or check the ones flagged above and try again."]);
    } finally {
      setPublishing(false);
    }
  };

  const visualsMeta = importResult?.visuals || [];
  const blueprintVisuals = importResult?.blueprint?.visuals || [];
  const includedCount = blueprintVisuals.filter(v => !excluded.has(v.chart_id)).length;
  const allAutoVerified = !!importResult?.allAutoVerified && excluded.size === 0;
  const duplicateCount = visualsMeta.filter(vm => vm.status === "duplicate").length;
  // roadmap #174: a VALID template that simply has no native charts (e.g. a pivot-only workbook)
  // now imports successfully — the backend returns `noChartsNotice: true` / `hasCharts: false`
  // with empty charts/visuals but populated `tableCandidates`/`sheetNames`. The wizard SKIPS the
  // chart-review step and lands on Tables with a gentle notice, instead of showing an empty
  // "Extracted 0 charts" confirm step with a dead Continue button. Fallback for an older backend
  // payload that lacks the flag: treat "ready, zero charts, but pivots exist" the same way.
  const noCharts = !!importResult && (
    importResult.noChartsNotice === true ||
    importResult.hasCharts === false ||
    (blueprintVisuals.length === 0 && (tableCandidates.length > 0 || (importResult.charts || []).length === 0))
  );
  // A tables-only template can publish with zero charts as long as at least one table is pinned;
  // a chart-bearing template keeps the original "need >=1 included chart" rule.
  // #188: publish also waits for every table's Excel check (and its confirmation when values
  // differ) and for every flagged chart's "publish without that filter" confirmation.
  const tablesGate = tablesReconcileGate(pinnedTables);
  const chartGate = chartFilterGate(visualsMeta, chartFilterAcks, excluded);
  const publishHint = !chartGate.canPublish ? "Confirm the flagged charts above (or remove them) before publishing."
    : tablesGate.checking ? "Checking your tables against your Excel…"
    : !tablesGate.canPublish ? "Review and confirm the differences from your Excel in the Tables step first."
    : "";
  const canPublish = (noCharts ? pinnedTables.length > 0 : includedCount > 0)
    && tablesGate.canPublish && chartGate.canPublish;

  // Closing this modal (backdrop click, "✕", Cancel, or navigating away entirely) is now SAFE
  // at ANY point — the upload/extract request lives in BlueprintImportContext (App-level), not
  // here, so it keeps running and its result keeps existing no matter what gets unmounted.
  // Reopening the wizard (from the banner's persistent indicator, AutoDeck.jsx) picks the result
  // right back up via getImport(projectId). Only an explicit "discard this result" (Cancel/close
  // once a result is READY) asks for confirmation and clears the slot — a still-uploading import
  // is left alone so it can finish and be found later.
  // In-app styled confirm (replaces the browser's native window.confirm, which shows a generic
  // "<site> says" header with no branding) — same shared component/pattern S3UploadPage.jsx and
  // AIInsightsPage.jsx already use for every other destructive confirmation in the app.
  const { confirm, confirmDialogProps } = useConfirm();

  const requestClose = async () => {
    if (importResult) {
      const ok = await confirm({
        title: "Discard this template?",
        message: "Your extracted charts won't be saved. This can't be undone.",
        confirmLabel: "Discard", cancelLabel: "Keep editing", tone: "danger",
      });
      if (!ok) return;
      discardTablesScratch();
      clearImport(projectId);
    }
    onClose();
  };

  return (
    <>
    <ConfirmDialog {...confirmDialogProps} />
    <div style={modalOverlay} onClick={requestClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            {step === "upload" ? "Upload Your Report Template"
              : step === "confirm" ? "Confirm Your Template"
              : step === "tables" ? "Add Summary Tables (optional)"
              : "Ready to Publish"}
          </div>
          <button onClick={requestClose} title={uploading ? "Close — it'll keep processing in the background" : "Close"}
            style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>

        {step === "upload" && (
          <>
            <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 16px", lineHeight: 1.5 }}>
              Upload the Excel (.xlsx/.xlsm) or PowerPoint (.pptx) file that already contains your report's charts.
              We'll extract them and match them to your project's data.
            </p>
            <div {...getRootProps()} style={{
              border: `1.5px dashed ${isDragActive ? "#7c5cff" : "#cbd5e1"}`, borderRadius: 10, padding: 32,
              textAlign: "center", background: isDragActive ? "#f5f3ff" : "#f8fafc", cursor: uploading ? "default" : "pointer",
            }}>
              <input {...getInputProps()} />
              <div style={{ fontSize: 28, marginBottom: 8 }}>📁</div>
              {uploading ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <RefreshCw size={18} className="animate-spin" style={{ color: "#7c5cff" }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{UPLOAD_STAGES[uploadStage]}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>This can take up to a minute for a large report with many charts. It's safe to close this and come back — it'll keep working.</div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Drag & drop your file here, or click to choose</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>Supports .xlsx, .xlsm, .pptx — max 50MB</div>
                </>
              )}
            </div>
            {error && (
              <div style={{ marginTop: 14, fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}>{error}</div>
            )}
          </>
        )}

        {step === "confirm" && (
          <>
            <div style={{ display: "flex", gap: 16, marginBottom: 6, fontSize: 12.5, color: "#64748b", flexWrap: "wrap" }}>
              <div>✅ Extracted <b style={{ color: "#0f172a" }}>{blueprintVisuals.length}</b> chart{blueprintVisuals.length === 1 ? "" : "s"}</div>
              {duplicateCount > 0 && (
                <div>· skipped <b style={{ color: "#0f172a" }}>{duplicateCount}</b> duplicate{duplicateCount === 1 ? "" : "s"}</div>
              )}
              <div>{allAutoVerified ? "✅ Everything matches your original report" : "⚠️ Review the items below before publishing"}</div>
            </div>
            {/* Real UX complaint, reported live: a customer couldn't tell what a confidence
                pill was even rating vs. what "Matches original" meant — two independent checks
                (which column we matched vs. whether the live number agrees with a saved
                snapshot) read as one confusing signal. One plain-language line, once, up front —
                no per-card repetition needed. */}
            <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 10, lineHeight: 1.5 }}>
              <b>Confidence</b> = how sure we are we matched the right column in your data.{" "}
              <b>"Matches original"</b> = the live number we computed agrees with what your file had saved.
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, fontSize: 11, color: "#94a3b8", flexWrap: "wrap" }}>
              <span>Match confidence per field:</span>
              <ConfidencePill confidence="high" /><ConfidencePill confidence="medium" /><ConfidencePill confidence="low" />
              <span style={{ color: "#cbd5e1" }}>— each chart lists its breakdown + data series below, hover a pill for details</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
              {visualsMeta.map(vm => {
                const spec = blueprintVisuals.find(v => v.chart_id === vm.chartId);
                const isExcluded = excluded.has(vm.chartId);
                const isDuplicate = vm.status === "duplicate";
                const unresolved = vm.status === "unresolved" || (!spec && !isDuplicate);
                const previewShown = previewOpen.has(vm.chartId);
                return (
                  <div key={vm.chartId} style={{
                    border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px",
                    background: isExcluded ? "#f8fafc" : "#fff", opacity: isExcluded ? 0.55 : 1,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        {spec ? (
                          <input
                            value={titleOverrides[vm.chartId] ?? spec.title ?? vm.chartId}
                            onChange={e => setTitleOverrides(t => ({ ...t, [vm.chartId]: e.target.value }))}
                            disabled={isExcluded}
                            style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", border: "1px solid transparent", borderRadius: 6, padding: "2px 4px", width: "100%", background: "transparent" }}
                          />
                        ) : isDuplicate ? (
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#94a3b8" }}>{vm.chartId} (same chart as {vm.duplicateOf || "another one"} above)</div>
                        ) : (
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#94a3b8" }}>{vm.chartId} (couldn't be matched to your data)</div>
                        )}
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                          {!unresolved && !isDuplicate && <ReconcileBadge status={vm.reconcile?.status} />}
                          {vm.auto_verified && <span style={{ fontSize: 11, fontWeight: 700, color: "#4f46e5" }}>✓ Auto-verified</span>}
                        </div>
                        <LabeledConfidenceList items={vm.labeledConfidences} fallback={vm.confidences} />
                        {/* fix-source-routing: which data this chart reads (backend wording). */}
                        {spec && !isDuplicate && sourceLabelFor(vm) && (
                          <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>{sourceLabelFor(vm)}</div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        {/* See the actual chart before publishing (real gap reported live: the
                            confirm screen showed only pills/badges, no way to see what a chart
                            will actually look like). Toggle per card, not always-open, so a
                            report with many charts doesn't force-render every graph at once. */}
                        {spec && !isExcluded && (
                          <button onClick={() => togglePreview(vm.chartId)} title={previewShown ? "Hide preview" : "Preview this chart"} style={{
                            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600,
                            padding: "5px 10px", borderRadius: 999, border: "1px solid #e2e8f0",
                            background: previewShown ? "#eef2ff" : "#fff", color: previewShown ? "#4f46e5" : "#64748b",
                            cursor: "pointer", whiteSpace: "nowrap",
                          }}>{previewShown ? <EyeOff size={12} /> : <Eye size={12} />} {previewShown ? "Hide" : "Preview"}</button>
                        )}
                        {spec && (
                          <button onClick={() => toggleExclude(vm.chartId)} style={{
                            fontSize: 11.5, fontWeight: 600, padding: "5px 10px", borderRadius: 999,
                            border: "1px solid #e2e8f0", background: isExcluded ? "#eef2ff" : "#fff",
                            color: isExcluded ? "#4f46e5" : "#64748b", cursor: "pointer", whiteSpace: "nowrap",
                          }}>{isExcluded ? "Include" : "Remove"}</button>
                        )}
                      </div>
                    </div>
                    {isDuplicate && (
                      <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>
                        This chart is identical to one already listed above — skipped so it doesn't publish as a duplicate card.
                      </div>
                    )}
                    {unresolved && (
                      <div style={{ fontSize: 11.5, color: "#b45309", marginTop: 6 }}>
                        Data not available for this view — it won't be published unless your data changes.
                      </div>
                    )}
                    {spec && !isExcluded && !isDuplicate && vm.source_warning && (
                      <div role="note" style={{ marginTop: 8, border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, padding: "8px 10px", fontSize: 11.5, color: "#92400e", lineHeight: 1.45 }}>
                        ⚠ {vm.source_warning}
                      </div>
                    )}
                    {/* #188: an Excel filter this chart can't apply — never silently dropped; the
                        customer confirms publishing without it (or removes the chart). */}
                    {spec && !isExcluded && !isDuplicate && (vm.filter_warnings || []).length > 0 && (
                      <div role="group" aria-label="Excel filter this chart can't apply" style={{ marginTop: 8, border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
                        {vm.filter_warnings.map((w, i) => (
                          <div key={`${w.field}-${i}`} style={{ fontSize: 11.5, color: "#92400e", lineHeight: 1.45 }}>⚠ {w.text}</div>
                        ))}
                        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#78350f", cursor: "pointer" }}>
                          <input type="checkbox" checked={chartFilterAcks.has(vm.chartId)} onChange={() => toggleChartFilterAck(vm.chartId)} style={{ marginTop: 2 }} />
                          <span>Publish this chart without that filter.</span>
                        </label>
                      </div>
                    )}
                    {previewShown && spec && !isExcluded && (
                      <div style={{ marginTop: 10, borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
                        <VisualPreview preview={vm.preview} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {publishErrors.length > 0 && (
              <div style={{ marginBottom: 14, fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}>
                {publishErrors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button onClick={() => setStep("upload")} disabled={publishing} style={{ ...btnSecondary, opacity: publishing ? 0.6 : 1 }}>← Back</button>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={requestClose} disabled={publishing} style={{ ...btnSecondary, opacity: publishing ? 0.6 : 1 }}>Cancel</button>
                <button onClick={() => setStep("tables")} disabled={includedCount === 0 || !chartGate.canPublish}
                  title={!chartGate.canPublish ? "Confirm or remove the flagged charts above first" : ""}
                  style={{ ...btnPrimary, opacity: (includedCount === 0 || !chartGate.canPublish) ? 0.6 : 1 }}>
                  Continue
                </button>
              </div>
            </div>
            {includedCount > 0 && !chartGate.canPublish && (
              <div role="status" style={{ fontSize: 11.5, color: "#64748b", marginTop: 8, textAlign: "right" }}>
                Confirm the flagged charts above (or remove them) to continue.
              </div>
            )}
          </>
        )}

        {step === "tables" && (
          <TablesStep
            call={call}
            noChartsNotice={noCharts}
            tablesScratchKey={tablesScratchKey}
            tableCandidates={tableCandidates}
            pinnedTables={pinnedTables}
            setPinnedTables={setPinnedTables}
            sheetNames={importResult?.sheetNames || []}
            slideCount={importResult?.slideCount || 0}
            staleTableNotices={staleTableNotices}
            onBack={noCharts ? null : () => setStep("confirm")}
            onSkip={() => setStep("publish")}
            onContinue={() => setStep("publish")}
          />
        )}

        {step === "publish" && (
          <>
            <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 16px", lineHeight: 1.5 }}>
              {noCharts ? (
                <>Ready to publish <b style={{ color: "#0f172a" }}>{pinnedTables.length}</b> table{pinnedTables.length === 1 ? "" : "s"} to your dashboard.</>
              ) : (
                <>
                  Ready to publish <b style={{ color: "#0f172a" }}>{includedCount}</b> chart{includedCount === 1 ? "" : "s"}
                  {pinnedTables.length > 0 && (
                    <> and <b style={{ color: "#0f172a" }}>{pinnedTables.length}</b> table{pinnedTables.length === 1 ? "" : "s"}</>
                  )} to your dashboard.
                </>
              )}
            </p>
            {publishErrors.length > 0 && (
              <div style={{ marginBottom: 14, fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}>
                {publishErrors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button onClick={() => setStep("tables")} disabled={publishing} style={{ ...btnSecondary, opacity: publishing ? 0.6 : 1 }}>← Back</button>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={requestClose} disabled={publishing} style={{ ...btnSecondary, opacity: publishing ? 0.6 : 1 }}>Cancel</button>
                <button onClick={publish} disabled={publishing || !canPublish} style={{ ...btnPrimary, opacity: (publishing || !canPublish) ? 0.6 : 1 }}>
                  {publishing ? "Publishing…"
                    : noCharts ? `Publish ${pinnedTables.length} table${pinnedTables.length === 1 ? "" : "s"}`
                    : allAutoVerified ? "Everything matches — publish" : `Publish (${includedCount})`}
                </button>
              </div>
            </div>
            {!publishing && publishHint && (
              <div role="status" style={{ fontSize: 11.5, color: "#64748b", marginTop: 8, textAlign: "right" }}>{publishHint}</div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
}
